import { useEffect, useState } from "react";
import { Logo, WaIcon, WA_URL } from "../lib/site";

const links = [
  { href: "#como-suena", label: "Cómo suena" },
  { href: "#como-funciona", label: "Cómo funciona" },
  { href: "#preguntas", label: "Preguntas" },
  { href: "#contacto", label: "Contacto" },
];

function MenuIcon({ open }) {
  return (
    <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      {open ? <path d="M4 4l12 12M16 4L4 16" /> : <path d="M2.5 6h15M2.5 14h15" />}
    </svg>
  );
}

function MobileMenu({ onClose }) {
  return (
    <nav
      id="menu-movil"
      aria-label="Navegación móvil"
      className="mmenu absolute inset-x-0 top-full border-b border-salvia/15 bg-hondo lg:hidden"
      data-testid="mobile-menu"
    >
      <div className="mx-auto max-w-7xl px-5 py-4 md:px-8">
        {links.map((l) => (
          <a
            key={l.href}
            href={l.href}
            onClick={onClose}
            data-testid={`mobile-nav-${l.href.slice(1)}`}
            className="flex min-h-[56px] items-center border-t border-salvia/10 font-display text-lg display-track first:border-t-0"
          >
            {l.label}
          </a>
        ))}
        <a
          href={WA_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Escríbenos por WhatsApp"
          onClick={onClose}
          data-testid="mobile-nav-whatsapp"
          className="flex min-h-[56px] items-center gap-3 border-t border-salvia/10 font-display text-lg display-track"
        >
          <WaIcon className="h-5 w-5" />
          <span>WhatsApp</span>
        </a>
      </div>
    </nav>
  );
}

function useScrolled() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return scrolled;
}

export default function Header() {
  const scrolled = useScrolled();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <header
      className={`site-header fixed top-0 left-0 right-0 z-50 text-crema${scrolled || open ? " scrolled" : ""}`}
      data-testid="site-header"
    >
      <div className="mx-auto flex h-full max-w-7xl items-center justify-between px-5 md:px-8">
        <a href="#top" className="flex items-center gap-3" data-testid="header-logo-link">
          <Logo className="h-7 w-7" />
          <span className="wordmark font-display text-[21px] display-track font-semibold">
            Kanvia<span className="serif-accent text-salvia" aria-hidden="true">.</span>
          </span>
        </a>
        <nav className="hidden items-center gap-8 lg:flex" aria-label="Navegación principal">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.href.slice(1)}`}
              className="nav-link font-display text-[14px] display-track opacity-75 transition-opacity duration-300 hover:opacity-100"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a
            href={WA_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Escríbenos por WhatsApp"
            data-testid="header-whatsapp-button"
            className="btn btn-line hidemob !min-h-[48px] !px-4 sm:inline-flex"
          >
            <WaIcon className="h-4 w-4" />
            <span>WhatsApp</span>
          </a>
          <a href="#contacto" data-testid="header-demo-button" className="btn btn-solid !min-h-[48px] !px-4">
            Empezar ahora
          </a>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="menu-movil"
            aria-label={open ? "Cerrar menú" : "Abrir menú"}
            data-testid="header-menu-button"
            className="btn btn-line !min-h-[48px] !w-12 !px-0 lg:!hidden"
          >
            <MenuIcon open={open} />
          </button>
        </div>
      </div>
      {open && <MobileMenu onClose={() => setOpen(false)} />}
    </header>
  );
}
